import { apiClient } from "./client";

// Mirrors backend/app/modules/weather/schemas.py — keep in lock-step.

export interface Subscription {
  id: string;
  block_id: string;
  provider_code: string;
  cadence_hours: number | null;
  is_active: boolean;
  last_successful_ingest_at: string | null;
  last_attempted_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface SubscriptionCreatePayload {
  provider_code: string;
  cadence_hours?: number | null;
}

export interface RefreshResponse {
  queued_farm_ids: string[];
  correlation_id: string | null;
}

export interface DailyForecast {
  date: string;
  high_c: string | null;
  low_c: string | null;
  precip_mm_total: string | null;
  precip_probability_max_pct: string | null;
  /** Daily MEAN relative humidity % (not a max). Null when the provider
   * returned no humidity hours for that day. */
  humidity_mean_pct: string | null;
}

export interface ForecastResponse {
  farm_id: string;
  provider_code: string;
  timezone: string;
  forecast_issued_at: string | null;
  days: DailyForecast[];
}

// --- Provider catalog -----------------------------------------------------

export interface WeatherProvider {
  code: string;
  name: string;
  kind: string;
}

export async function listWeatherProviders(): Promise<WeatherProvider[]> {
  const { data } = await apiClient.get<WeatherProvider[]>("/v1/weather/providers");
  return data;
}

// --- Subscriptions --------------------------------------------------------

export async function listSubscriptions(
  blockId: string,
  options: { include_inactive?: boolean } = {},
): Promise<Subscription[]> {
  const { data } = await apiClient.get<Subscription[]>(
    `/v1/blocks/${blockId}/weather/subscriptions`,
    { params: { include_inactive: options.include_inactive ?? false } },
  );
  return data;
}

export async function createSubscription(
  blockId: string,
  payload: SubscriptionCreatePayload,
): Promise<Subscription> {
  const { data } = await apiClient.post<Subscription>(
    `/v1/blocks/${blockId}/weather/subscriptions`,
    payload,
  );
  return data;
}

export async function revokeSubscription(blockId: string, subscriptionId: string): Promise<void> {
  await apiClient.delete(`/v1/blocks/${blockId}/weather/subscriptions/${subscriptionId}`);
}

// --- Refresh + Forecast ---------------------------------------------------

export async function triggerRefresh(blockId: string): Promise<RefreshResponse> {
  const { data } = await apiClient.post<RefreshResponse>(`/v1/blocks/${blockId}/weather/refresh`);
  return data;
}

export async function getForecast(
  blockId: string,
  options: { horizon_days?: number; provider_code?: string } = {},
): Promise<ForecastResponse> {
  const { data } = await apiClient.get<ForecastResponse>(`/v1/blocks/${blockId}/weather/forecast`, {
    params: {
      horizon_days: options.horizon_days ?? 5,
      provider_code: options.provider_code ?? "open_meteo",
    },
  });
  return data;
}

// --- Daily derived (historical) ------------------------------------------

export interface DerivedDailyRead {
  date: string;
  temp_min_c: string | null;
  temp_max_c: string | null;
  temp_mean_c: string | null;
  precip_mm_daily: string | null;
  precip_mm_7d: string | null;
  precip_mm_30d: string | null;
  et0_mm_daily: string | null;
  gdd_base10: string | null;
  gdd_base15: string | null;
  gdd_cumulative_base10_season: string | null;
  computed_at: string;
}

export async function getDerivedDaily(
  blockId: string,
  options: { since: string; until: string },
): Promise<DerivedDailyRead[]> {
  const { data } = await apiClient.get<DerivedDailyRead[]>(
    `/v1/blocks/${blockId}/weather/derived`,
    { params: { since: options.since, until: options.until } },
  );
  return data;
}
